import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OverviewResponse } from 'api/new-analytics/types';
import LineChart from 'app/modules/analytics/components/shared/charts/LineChart';
import { buildTimeAxis } from 'app/modules/analytics/modules/performance/utils/timeSeriesHelpers';
import MetricStatTile from 'app/modules/analytics/components/shared/MetricStatTile';
import SummaryProfileHeader from 'app/modules/analytics/components/shared/SummaryProfileHeader';
import { useReportPreviewRenderReadyBlock } from 'app/modules/analytics/modules/reports/context/ReportPreviewRenderReadyContext';
import {
  OVERVIEW_SUMMARY_METRIC_LABELS,
  OVERVIEW_SUMMARY_METRIC_ORDER,
  type OverviewSummaryMetricKey,
} from 'app/modules/analytics/utils/overviewSummaryMetrics';
import {
  formatAnalyticsPeriodLabel,
  getOverviewComparisonCaption,
} from 'app/modules/analytics/utils/overviewSummaryCaption';

export interface ReportPreviewOverviewProfileMeta {
  name?: string;
  logoSrc?: string;
  pageType?: number;
}

interface SummaryMetricChartProps {
  metricKey: OverviewSummaryMetricKey;
  data: OverviewResponse;
  isTracking: boolean;
}

function SummaryMetricChart({
  metricKey,
  data,
  isTracking,
}: SummaryMetricChartProps) {
  const { t } = useTranslation();
  const blockId = `overview-summary-chart-${metricKey}`;
  const timeSeries = useMemo(
    () => data.summary?.time_series?.[metricKey] ?? [],
    [data.summary?.time_series, metricKey],
  );
  const { markReady } = useReportPreviewRenderReadyBlock(blockId, {
    enabled: timeSeries.length > 0,
    immediateReady: timeSeries.length === 0,
  });

  const chartAxis = useMemo(
    () => buildTimeAxis(timeSeries.map((point) => point.date)),
    [timeSeries],
  );
  const chartData = useMemo(
    () => timeSeries.map((point) => point.value),
    [timeSeries],
  );

  const label = t(metricKey === 'posts_count' ? 'posts' : metricKey, {
    defaultValue: OVERVIEW_SUMMARY_METRIC_LABELS[metricKey],
  });

  const chartRenderProps = isTracking
    ? {
        disableAnimation: true,
        onRendered: markReady,
      }
    : {};

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-2"
      data-cy={`analytics-overview-export_metric-chart-${metricKey}`}
    >
      <span className="tw-text-xs tw-font-semibold tw-text-grayscale-180">
        {label}
      </span>
      {timeSeries.length > 0 ? (
        <LineChart
          labels={chartAxis.labels}
          tooltipTitles={chartAxis.tooltipTitles}
          datasets={[
            {
              label: OVERVIEW_SUMMARY_METRIC_LABELS[metricKey],
              data: chartData,
            },
          ]}
          height={100}
          yAxisPosition="right"
          fill
          {...chartRenderProps}
        />
      ) : (
        <div className="tw-flex tw-h-[100px] tw-items-center tw-justify-center tw-rounded-lg tw-bg-grayscale-5">
          <span className="tw-text-sm tw-text-grayscale-100">
            {t('noDataAvailable', { defaultValue: 'No data available' })}
          </span>
        </div>
      )}
    </div>
  );
}

interface Props {
  data: OverviewResponse;
  profileMeta: ReportPreviewOverviewProfileMeta;
  isComparisonEnabled: boolean;
}

const ReportPreviewOverviewSummary = ({
  data,
  profileMeta,
  isComparisonEnabled,
}: Props) => {
  const { t } = useTranslation();
  const summaryTotals = data.summary?.totals;

  return (
    <div
      className="tw-overflow-hidden tw-rounded-xl"
      style={{
        background:
          'linear-gradient(276.6deg, rgba(121,54,204,0.2) 2.37%, rgba(131,54,162,0.2) 50.41%, rgba(202,56,86,0.2) 97.55%)',
      }}
      data-cy="analytics-overview-export_summary-block"
    >
      <div className="tw-px-4 tw-py-3">
        <span className="tw-text-base tw-font-semibold tw-text-grayscale-180">
          {t('summary', { defaultValue: 'Summary' })}
        </span>
      </div>

      <div className="tw-overflow-hidden tw-rounded-xl tw-bg-white tw-p-4 tw-shadow-sm">
        <SummaryProfileHeader
          name={profileMeta.name}
          logoSrc={profileMeta.logoSrc}
          pageType={profileMeta.pageType}
          periodLabel={formatAnalyticsPeriodLabel(
            data.period?.since,
            data.period?.until,
          )}
        />

        {summaryTotals ? (
          <div className="analytics-report-overview-kpi-row tw-mb-4 tw-flex tw-flex-wrap tw-gap-2">
            {OVERVIEW_SUMMARY_METRIC_ORDER.map((key) => {
              const metric = summaryTotals[key];
              return (
                <MetricStatTile
                  key={key}
                  label={t(key === 'posts_count' ? 'posts' : key, {
                    defaultValue: OVERVIEW_SUMMARY_METRIC_LABELS[key],
                  })}
                  value={metric?.value ?? null}
                  changePercent={metric?.change_percent ?? null}
                  caption={getOverviewComparisonCaption(
                    t,
                    metric?.previous_value,
                    isComparisonEnabled,
                  )}
                  dataCy={`analytics-overview-export_metric-pill-${key}`}
                />
              );
            })}
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-6">
            <span className="tw-text-sm tw-text-grayscale-100">
              {t('noDataAvailable', { defaultValue: 'No data available' })}
            </span>
          </div>
        )}

        <div className="analytics-report-overview-chart-grid tw-grid tw-grid-cols-2 tw-gap-4">
          {OVERVIEW_SUMMARY_METRIC_ORDER.map((key) => (
            <SummaryMetricChart
              key={key}
              metricKey={key}
              data={data}
              isTracking
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewOverviewSummary;
